import { optionColor } from './optionColor'

/**
 * The one option visual — coloured bullet + label. Shared by the select read
 * content, its editor trigger and option rows, and a kanban lane title, so they
 * can't drift. The bullet takes the OPTION's colour (owner decision
 * 2026-09-24) from the attribute's declared `config.options` — see
 * optionColor — so the same value is the same colour on every surface.
 */
/** The option's colour mark alone — a compacted option chip (SEN-205). */
export function OptionBullet({ value, options }: { value: string; options?: unknown }) {
  return (
    <span
      data-testid="option-bullet"
      className="h-2 w-2 shrink-0 rounded-full"
      style={{ backgroundColor: optionColor(value, options) }}
    />
  )
}

/** A stored slug in words ("in_progress" → "In progress"): the last resort for a value no declared option names.
 *  Anything that is not a plain lowercase slug (a name, an id, an email) is returned as it is. */
export function slugToWords(value: string): string {
  if (!/^[a-z][a-z0-9]*(?:[_-][a-z0-9]+)*$/.test(value)) return value
  const words = value.replace(/[_-]+/g, ' ')
  return words[0].toUpperCase() + words.slice(1)
}

/** The option's DECLARED label (`config.options[].label`, monad/select_options.py); a value the options do not
 *  declare is shown in words, never as its slug (SEN-439 X5). */
export function declaredOptionLabel(value: string, options?: unknown): string {
  if (!Array.isArray(options)) return slugToWords(value)
  const option = options.find((o) => o && typeof o === 'object' && (o as { id?: unknown }).id === value) as
    { label?: unknown } | undefined
  return typeof option?.label === 'string' && option.label ? option.label : slugToWords(value)
}

export function OptionLabel({ value, options }: { value: string; options?: unknown }) {
  return (
    <span data-option={value} className="inline-flex min-w-0 items-center gap-1.5">
      <OptionBullet value={value} options={options} />
      <span className="truncate">{declaredOptionLabel(value, options)}</span>
    </span>
  )
}
